// @flow

// react-hook-form reports errors keyed by field name; the wording the agent
// needs is the label already on each field definition. Joining the two here is
// what keeps a relabelled field from drifting out of the banner.
export const getMissingFieldLabels = (
  sectionFields: { [string]: Array<Object> },
  errors: ?Object,
): Array<string> => {
  const errored = new Set(Object.keys(errors ?? {}));

  return Object.keys(sectionFields)
    .flatMap((section) => sectionFields[section])
    .filter(({ name, label }) => Boolean(name && label && errored.has(name)))
    .map(({ label }) => label);
};

// The fields the modal renders as bare Controllers rather than handing to the
// library `Form`. They have no definition in `sectionFields`, so
// `getMissingFieldLabels` cannot name them and each carries its own copy.
// Named explicitly rather than taken as the complement of `sectionFields`: the
// complement would splice any future field's raw error text straight into a
// user-facing Banner, and swallow one whose rule wrote no message at all.
export const STANDALONE_ERROR_FIELDS = ['selectedPrograms'];

// The other half of the same rule.
export const getStandaloneErrorMessages = (
  sectionFields: { [string]: Array<Object> },
  errors: ?Object,
): Array<string> => {
  const named = new Set(
    Object.keys(sectionFields)
      .flatMap((section) => sectionFields[section])
      .map(({ name }) => name),
  );

  return STANDALONE_ERROR_FIELDS.filter(
    (name) => !named.has(name) && errors?.[name],
  )
    .map((name) => errors?.[name]?.message)
    .filter(Boolean);
};

// The date controls hand back whatever the picker holds, and a half-typed or
// deleted date leaves an Invalid Date behind rather than an empty value. That
// is truthy, so it slips past both a `required` rule and a plain truthiness
// check, and only turns into `null` at the point it is serialized — which the
// routes refuse. Both the field rule and the payload ask here instead.
export const isValidDate = (value: mixed): boolean => {
  if (!value) {
    return false;
  }

  // A dayjs or moment instance answers `toDate`; anything else — a Date, or
  // the ISO string the record arrives as — is handed to Date to parse.
  const date =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : new Date((value: any));

  return date instanceof Date && !Number.isNaN(date.getTime());
};
